'use client';

import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import ReviewCard from "@/components/ReviewCard";
import {ChevronLeft, ChevronRight, Star} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";

interface Review {
    _id: string;
    author: string;
    rating: number;
    reviewText: string;
    profileImage: string;
}

interface ReviewProps {
    initialReviews?: Review[];
}

const TrustBadgeIcon = () => (
    <svg width="47" height="52" viewBox="0 0 47 52" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M22.7549 1.70215C23.2401 1.5506 23.7599 1.5506 24.2451 1.70215L24.4844 1.79004L43.5244 9.9502C44.5482 10.3891 45.1586 11.4523 45.0205 12.5576L42.5322 32.4619C42.2963 34.3488 41.3524 36.0756 39.8916 37.293L25.1006 49.6191C24.1735 50.3917 22.8265 50.3917 21.8994 49.6191L7.1084 37.293C5.64763 36.0756 4.70369 34.3488 4.46777 32.4619L1.97949 12.5576C1.84143 11.4523 2.45181 10.3891 3.47559 9.9502L22.5156 1.79004L22.7549 1.70215Z"
            fill="#7E869E" fillOpacity="0.25" stroke="#222222" strokeWidth="3"
            strokeLinecap="round"
        />
        <path d="M14.875 26L23.0687 34.1937C23.2918 34.4168 23.6633 34.3801 23.8383 34.1175L35 17.375"
              stroke="#222222" strokeWidth="3" strokeLinecap="round"
        />
    </svg>
);

const GoogleLogo = () => (
    <svg width="140" height="32" viewBox="0 0 188 32" fill="none"
         xmlns="http://www.w3.org/2000/svg">
        <g clipPath="url(#clip0_622_824)">
            <path
                d="M41.3524 16.4105C41.3524 20.8522 37.8265 24.1252 33.4994 24.1252C29.1724 24.1252 25.6465 20.8522 25.6465 16.4105C25.6465 11.9374 29.1724 8.69568 33.4994 8.69568C37.8265 8.69568 41.3524 11.9374 41.3524 16.4105ZM37.9147 16.4105C37.9147 13.6348 35.8712 11.7357 33.4994 11.7357C31.1277 11.7357 29.0841 13.6348 29.0841 16.4105C29.0841 19.1583 31.1277 21.0852 33.4994 21.0852C35.8712 21.0852 37.9147 19.1548 37.9147 16.4105Z"
                fill="#313131"/>
            <path
                d="M58.2938 16.4105C58.2938 20.8522 54.7679 24.1252 50.4408 24.1252C46.1138 24.1252 42.5879 20.8522 42.5879 16.4105C42.5879 11.9409 46.1138 8.69568 50.4408 8.69568C54.7679 8.69568 58.2938 11.9374 58.2938 16.4105ZM54.8561 16.4105C54.8561 13.6348 52.8126 11.7357 50.4408 11.7357C48.0691 11.7357 46.0255 13.6348 46.0255 16.4105C46.0255 19.1583 48.0691 21.0852 50.4408 21.0852C52.8126 21.0852 54.8561 19.1548 54.8561 16.4105Z"
                fill="#313131"/>
            <path
                d="M74.5285 9.16177V23.0122C74.5285 28.7096 71.1191 31.0366 67.0885 31.0366C63.2944 31.0366 61.0109 28.5357 60.1497 26.4905L63.1427 25.2626C63.6756 26.5183 64.9815 28 67.085 28C69.665 28 71.2638 26.4313 71.2638 23.4783V22.3687H71.1438C70.3744 23.3044 68.8921 24.1218 67.0215 24.1218C63.1074 24.1218 59.5215 20.7618 59.5215 16.4383C59.5215 12.0835 63.1074 8.69568 67.0215 8.69568C68.8885 8.69568 70.3709 9.51307 71.1438 10.4209H71.2638V9.16524H74.5285V9.16177ZM71.5074 16.4383C71.5074 13.7218 69.6685 11.7357 67.3285 11.7357C64.9568 11.7357 62.9697 13.7218 62.9697 16.4383C62.9697 19.127 64.9568 21.0852 67.3285 21.0852C69.6685 21.0852 71.5074 19.127 71.5074 16.4383Z"
                fill="#313131"/>
            <path d="M79.9115 1.04346V23.6522H76.5586V1.04346H79.9115Z" fill="#313131"/>
            <path
                d="M92.9778 18.9495L95.6461 20.7026C94.7849 21.9582 92.7096 24.1217 89.1237 24.1217C84.6766 24.1217 81.3555 20.7339 81.3555 16.4069C81.3555 11.8191 84.7049 8.69214 88.739 8.69214C92.8014 8.69214 94.7884 11.8782 95.4378 13.6L95.7943 14.4765L85.3296 18.7478C86.1308 20.2956 87.3766 21.0852 89.1237 21.0852C90.8743 21.0852 92.0884 20.2365 92.9778 18.9495ZM84.7649 16.1739L91.7602 13.3113C91.3755 12.3478 90.2178 11.6765 88.8555 11.6765C87.1084 11.6765 84.6766 13.1965 84.7649 16.1739Z"
                fill="#313131"/>
            <path
                d="M12.9552 14.4035V11.1304H24.147C24.2564 11.7008 24.3128 12.3756 24.3128 13.1061C24.3128 15.5617 23.6317 18.5982 21.4364 20.7617C19.3011 22.953 16.5728 24.1217 12.9587 24.1217C6.25989 24.1217 0.626953 18.7443 0.626953 12.1426C0.626953 5.54084 6.25989 0.163452 12.9587 0.163452C16.6646 0.163452 19.3046 1.5965 21.2881 3.46432L18.9446 5.77389C17.5222 4.45911 15.5952 3.4365 12.9552 3.4365C8.06342 3.4365 4.23754 7.32171 4.23754 12.1426C4.23754 16.9635 8.06342 20.8487 12.9552 20.8487C16.1281 20.8487 17.9352 19.593 19.0928 18.4521C20.0317 17.5269 20.6493 16.2052 20.8928 14.4L12.9552 14.4035Z"
                fill="#313131"/>
        </g>
        <path d="M111.5 3V29" stroke="black"/>
        <path
            d="M135.764 20.948H127.316V18.5L135.188 7.7H139.004V18.236H141.116V20.948H139.004V24.5H135.764V20.948ZM135.908 18.236V11.156L130.892 18.236H135.908ZM145.443 24.668C144.851 24.668 144.363 24.484 143.979 24.116C143.611 23.732 143.427 23.276 143.427 22.748C143.427 22.204 143.611 21.748 143.979 21.38C144.363 20.996 144.851 20.804 145.443 20.804C146.019 20.804 146.491 20.996 146.859 21.38C147.243 21.748 147.435 22.204 147.435 22.748C147.435 23.276 147.243 23.732 146.859 24.116C146.491 24.484 146.019 24.668 145.443 24.668ZM155.896 7.532C157.096 7.532 158.168 7.788 159.112 8.3C160.072 8.812 160.824 9.516 161.368 10.412C161.928 11.308 162.208 12.332 162.208 13.484C162.208 14.38 162.024 15.284 161.656 16.196C161.304 17.108 160.752 18.044 160 19.004L155.752 24.5H152.2L157.192 18.14L160.312 14.828C160.024 16.092 159.432 17.1 158.536 17.852C157.64 18.588 156.56 18.956 155.296 18.956C154.224 18.956 153.256 18.732 152.392 18.284C151.544 17.82 150.872 17.172 150.376 16.34C149.88 15.508 149.632 14.532 149.632 13.412C149.632 12.26 149.912 11.244 150.472 10.364C151.032 9.484 151.784 8.796 152.728 8.3C153.672 7.788 154.728 7.532 155.896 7.532ZM158.92 13.412C158.92 12.772 158.8 12.22 158.56 11.756C158.32 11.292 157.968 10.932 157.504 10.676C157.056 10.42 156.52 10.292 155.896 10.292C155.256 10.292 154.712 10.42 154.264 10.676C153.832 10.932 153.496 11.292 153.256 11.756C153.032 12.22 152.92 12.772 152.92 13.412C152.92 14.036 153.032 14.58 153.256 15.044C153.496 15.508 153.832 15.868 154.264 16.124C154.712 16.364 155.256 16.484 155.896 16.484C156.52 16.484 157.056 16.364 157.504 16.124C157.968 15.868 158.32 15.508 158.56 15.044C158.8 14.58 158.92 14.036 158.92 13.412ZM169.08 5.3H172.272L166.488 26.9H163.32L169.08 5.3ZM184.681 7.7V10.412H177.121V14.444C177.441 14.044 177.865 13.724 178.393 13.484C178.937 13.244 179.585 13.124 180.337 13.124C181.329 13.124 182.177 13.3 182.881 13.652C183.585 14.004 184.153 14.46 184.585 15.02C185.033 15.564 185.353 16.164 185.545 16.82C185.753 17.476 185.857 18.108 185.857 18.716C185.857 19.868 185.609 20.892 185.113 21.788C184.633 22.684 183.937 23.388 183.025 23.9C182.129 24.412 181.049 24.668 179.785 24.668C178.537 24.668 177.465 24.46 176.569 24.044C175.689 23.612 174.993 23.028 174.481 22.292C173.985 21.556 173.681 20.716 173.569 19.772H176.737C176.881 20.396 177.209 20.908 177.721 21.308C178.233 21.708 178.905 21.908 179.737 21.908C180.393 21.908 180.921 21.78 181.321 21.524C181.737 21.268 182.049 20.908 182.257 20.444C182.465 19.964 182.569 19.396 182.569 18.74C182.569 17.812 182.321 17.092 181.825 16.58C181.329 16.068 180.625 15.812 179.713 15.812C179.025 15.812 178.449 15.996 177.985 16.364C177.521 16.716 177.193 17.172 177.001 17.732H173.881V7.7H184.681Z"
            fill="black"/>
        <defs>
            <clipPath id="clip0_622_824">
                <rect width="96" height="32" fill="white" transform="translate(0.5)"/>
            </clipPath>
        </defs>
    </svg>
);

const StarRating = ({ rating = 5 }: { rating?: number }) => (
    <div className="flex gap-x-4">
        {[...Array(5)].map((_, i) => (
            <Star
                key={i}
                size={14}
                color="#FFB800"
                fill={i < rating ? "#FFB800" : "none"}
                strokeWidth={1.75}
            />
        ))}
    </div>
);

const LoadingSpinner = () => (
    <div className="flex justify-center items-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
    </div>
);

const ErrorMessage = ({ message }: { message: string }) => (
    <div className="flex justify-center items-center py-20">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700">{message}</p>
        </div>
    </div>
);

export default function Review({ initialReviews }: ReviewProps) {
    const hasInitialReviews = Array.isArray(initialReviews) && initialReviews.length > 0;
    const hasFetchedReviews = useRef(false);
    const [reviews, setReviews] = useState<Review[]>(() => initialReviews ?? []);
    const [loading, setLoading] = useState(!hasInitialReviews);
    const [error, setError] = useState<string | null>(null);
    const [carouselApi, setCarouselApi] = useState<any>();


    React.useEffect(() => {
        if (!carouselApi) {
            return;
        }
    }, [carouselApi]);

    useEffect(() => {
        if (!hasInitialReviews || !initialReviews) {
            return;
        }

        setReviews(initialReviews);
        setError(null);
        setLoading(false);
    }, [hasInitialReviews, initialReviews]);

    useEffect(() => {
        if (hasInitialReviews || hasFetchedReviews.current) {
            return;
        }

        hasFetchedReviews.current = true;
        setError(null);

        const fetchReviews = async () => {
            try {
                setLoading(true);
                const response = await fetch('/api/reviews', {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                });

                if (!response.ok) {
                    throw new Error(`Errore nel caricamento delle recensioni: ${response.status}`);
                }

                const data = await response.json();
                setReviews(data.reviews || []);
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : 'Errore sconosciuto';
                setError(errorMessage);
                console.error('Errore nel recupero delle recensioni:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchReviews();
    }, [hasInitialReviews]);

    if (loading) {
        return <LoadingSpinner />;
    }

    if (error) {
        return <ErrorMessage message={error} />;
    }

    return (
        <>
            {/* Header Section */}
            <section className="">
                <div className="mx-auto">
                    <div className="flex items-center justify-center gap-x-4 mb-8">
                        <TrustBadgeIcon />
                        <h2 className="text-4xl font-bold text-gray-900 max-sm:text-xl">
                            Le recensioni dei nostri clienti
                        </h2>
                    </div>

                    {/* Reviews Carousel */}
                    <div className="relative">
                        <div className="bg-[#F4F4F4] relative top-20 w-full h-[350px] rounded-lg"></div>
                        <div className="h-max py-2 mx-auto absolute inset-0 top-0 left-0 right-0 bottom-0 m-auto">
                            <Carousel
                                className="w-full mx-auto my-5"
                                opts={{
                                    align: "center",
                                    loop: reviews.length > 3,
                                }}
                                setApi={setCarouselApi}
                            >
                                <CarouselContent className="-ml-2 md:-ml-4">
                                    {reviews.length > 0 ? (
                                        reviews.map((review, index) => (
                                            <CarouselItem
                                                key={review._id || index}
                                                className="pr-3 md:pr-4 basis-[280px] sm:basis-[320px] md:basis-[350px] lg:basis-[380px]"
                                            >
                                                <div className="py-5">
                                                    <ReviewCard
                                                        name={review.author}
                                                        review={review.reviewText}
                                                        image={review.profileImage || "/default-avatar.png"}
                                                    />
                                                </div>
                                            </CarouselItem>
                                        ))
                                    ) : (
                                        <div className="text-center py-20 w-full">
                                            <p className="text-gray-500">Nessuna recensione disponibile</p>
                                        </div>
                                    )}
                                </CarouselContent>
                            </Carousel>
                        </div>
                    </div>
                </div>
            </section>

            {/* Google Rating Section */}
            <section className="bg-[#F4F4F4] pb-2 relative">
                <div className="container mx-auto px-2 relative">
                    {/* Left Arrow */}
                    <button
                        onClick={() => carouselApi?.scrollPrev()}
                        className="absolute left-4 top-1/2 -translate-y-1/2 h-20 w-20 text-gray-600 flex max-sm:justify-start items-center justify-center rounded-full z-10"
                    >
                        <ChevronLeft size={30} />
                    </button>

                    <div className="flex flex-col justify-center gap-y-4 items-center">
                        <StarRating rating={5} />
                        <div className="flex items-center gap-x-3">
                            <GoogleLogo />
                            <span className="text-2xl font-semibold">4.9/5</span>
                        </div>
                        <div className="text-center">
                            <p className="font-bold text-xs underline">100% verificabile</p>
                        </div>
                    </div>

                    {/* Right Arrow */}
                    <button
                        onClick={() => carouselApi?.scrollNext()}
                        className="absolute right-4 top-1/2 -translate-y-1/2  h-20 w-20 text-gray-600 flex  max-sm:justify-end items-center justify-center rounded-full z-10"
                    >
                        <ChevronRight size={30} />
                    </button>
                </div>
            </section>
        </>
    );
}
